/**
 * Board renderer — PixiJS v8 (WebGL), behind the BoardView contract (geometry.ts)
 * the UI (game.ts / main.ts / buy.ts) talks to.
 *
 * The camera is a single `world` Container transform (scale + position) mapping
 * down-positive world units (hc()) to CSS px; Pixi re-renders the retained scene
 * graph on the GPU, so a pan/zoom is just a transform change. Rendering is
 * on-demand: the ticker stays stopped while idle and runs only for the duration
 * of a move tween, so an unchanging board is never redrawn 60×/sec.
 *
 * Layers (back → front): sea · shore foam · land tiles · beaches · grid ·
 * scenery · city · ships · buoys · routes · selection · fog (inverse-masked) ·
 * city flags. City NAME/VP/build text is a DOM overlay (crisp subpixel AA), and
 * the minimap stays a small Canvas2D HUD thumbnail — both positioned over the
 * Pixi canvas. The animated sea (a GPU water shader) is Phase 2; for now the sea
 * is a flat depth colour.
 */
import { Application, Container, Graphics, Sprite, TilingSprite, Texture, TextureSource, Assets } from './pixi';
import { ICON_VP } from '../ui/icons';
import { visibleOnBoard, tileInView } from '../rules/fog';
import type { GameState, Ship, Side } from '../rules/state';
import {
  HEX, SQ3, BORDER, ISO_Y, PALETTE, sideColor, sideColorLight, SHIP_SCALE, shipSpriteUrl,
  ensignUrl, ensignLayout, sideFacing,
  hexCorners, edgeNeighbor, hc, extentOf, allSpriteUrls, drawMinimap, minimapSize,
  type BoardView, type BoardOpts, type BoardDynamic,
} from './geometry';

// Re-export the shared contract so the UI can import it straight from the renderer.
export { minimapSize } from './geometry';
export type { BoardView, BoardOpts, BoardDynamic } from './geometry';

const FONT_STACK = 'Verdana, "Segoe UI", system-ui, Arial, sans-serif';
const TEXT_HALO = '0 0 2px rgba(0,0,0,0.95), 0 1px 1px rgba(0,0,0,0.9)';
const SEA_FILL = '#4a9fbe'; // flat base colour under the water texture (fallback tint)
const SEA_TILE_WU = HEX * 6; // on-board size of one sea texture tile, in world units
const SEA_TEX_ALPHA = 0.32; // water texture opacity; the flat SEA_FILL shows through so the sea recedes behind the ships/cities
// Cheap "moving water": the tiling texture scrolls slowly (world units per
// second), so the swell drifts without any shader. Gentle so it reads as a lazy
// current, not a scroll. (Negative tilePosition.y drifts the swell UP the screen.)
const SEA_DRIFT_X = 0; const SEA_DRIFT_Y = -2;
// Idle sea drift renders at this frame rate. The drift is only ~2 units/sec, so
// ~10fps is visually smooth (sub-pixel per frame) yet re-renders the board a third
// as often as 30fps. A move tween temporarily bumps the ticker back to full rate.
const SEA_IDLE_FPS = 10;

interface CityLabel { plate: HTMLDivElement; name: HTMLSpanElement; vp: HTMLSpanElement; }

export async function buildBoardScene(
  container: HTMLElement,
  g: GameState,
  opts: BoardOpts,
): Promise<BoardView> {
  const viewSide: Side = opts.viewSide ?? 'R';
  const responsive = opts.responsive ?? false;
  const maxZoom = opts.maxZoom ?? 3;
  const automated = navigator.webdriver;
  const wantAnim = (opts.animate ?? false) && !automated;
  // Supersample to ≥2× (render large, downscale): the only thing that makes the
  // texture-edge coastline and sprite silhouettes genuinely crisp — MSAA misses
  // texture edges and FXAA just blurs detail. Cost is bounded because the board
  // renders on demand, not 60fps. Automation stays 1× so goldens are deterministic.
  const resolution = automated ? 1 : Math.max(window.devicePixelRatio || 1, 2);

  // ---- Pixi application + canvas --------------------------------------
  const canvas = document.createElement('canvas');
  canvas.style.display = 'block';
  const app = new Application();
  await app.init({
    canvas,
    width: opts.width,
    height: opts.height,
    backgroundColor: PALETTE.outOfBounds,
    antialias: true,
    resolution,
    autoDensity: true,
    preference: 'webgl',
  });
  container.style.position ||= 'relative';
  container.appendChild(canvas);

  // textures (PNGs, loaded once; update() is then synchronous). Generate mipmaps so
  // the high-res sprite PNGs (~1280px) minify cleanly to their ~50px on-board size —
  // without them, linear sampling undersamples a 25:1 downscale and edges read as
  // aliased/"pixelated". autoGenerateMipmaps is read-only post-construction, so it
  // must be set on the default options BEFORE the textures are created/uploaded.
  TextureSource.defaultOptions.autoGenerateMipmaps = true;
  await Assets.load(allSpriteUrls(g));
  const tex = (url: string): Texture => Assets.get(url) as Texture;

  // Seamless tiling water texture (a static "deep-swell" render, loaded via
  // allSpriteUrls) drawn over the flat SEA_FILL base. Static, so the board keeps
  // its render-on-demand loop (zero idle cost); it wraps with no seam.
  let seaTex: Texture | null = null;
  try { seaTex = tex('assets/sprites/sea.jpg'); } catch { seaTex = null; }
  // The scrolling water tile (set in buildStatic). Animated only when motion is
  // wanted AND we have a texture — automation keeps it null-driven/frozen.
  let seaSprite: TilingSprite | null = null;
  const seaAnimate = wantAnim && seaTex != null;

  // ---- terrain classification (static) --------------------------------
  // City hexes are LAND now: they render the grass tile like any other land (and
  // carry the walled-town overlay on top), so they go into landTiles. They're
  // excluded from `inlandGrass` (the tree/mountain scatter) so scenery never
  // sprouts through the town.
  const cityKey = new Set(g.cities.map((ct) => `${ct.c},${ct.r}`));
  const isOpenWater = (c: number, r: number): boolean =>
    r >= 0 && r < g.H && c >= 0 && c < g.W && g.tiles[r]![c] === 'water';
  const landTiles: [number, number][] = [];
  const inlandGrass: [number, number][] = [];
  for (let r = 0; r < g.H; r++) {
    for (let c = 0; c < g.W; c++) {
      if (g.tiles[r]![c] === 'land') {
        landTiles.push([c, r]);
        if (!cityKey.has(`${c},${r}`)) inlandGrass.push([c, r]);
      }
    }
  }

  const ext = extentOf(g, BORDER);
  const playExt = extentOf(g, 0);

  // ---- camera ----------------------------------------------------------
  let aspect = opts.width / opts.height;
  let curW = opts.width;
  let curH = opts.height;
  let zoomLevel = 1; // the UI level (1 = fit whole board … maxZoom = full zoom-in)
  let camLeft = 0; let camRight = 0; let camTop = 0; let camBot = 0;
  let cx = playExt.cx; let cy = playExt.cy;
  // Fit to the MAP hull (playExt), not ext — ext carries a full BORDER-ring of now-
  // empty out-of-bounds space (sea + fog stop at the hull), which at zoom 1 just
  // framed the map with wasted margin. A small pad keeps the ice rim off the edge.
  const FIT_PAD = HEX * 0.35;
  const boardHalfW = playExt.w / 2 + FIT_PAD;
  const boardHalfH = playExt.h / 2 + FIT_PAD;
  const baseHalf = (): number => (responsive ? Math.max(boardHalfH, boardHalfW / aspect) : boardHalfH);
  // The fit view (zoomLevel 1) is much further out on a narrow viewport, because
  // baseHalf grows to cram the board's width in. Left as-is, the same level reads
  // as far less magnification than on a wide screen. So ramp the effective zoom by
  // the aspect penalty p = baseHalf/boardHalfH (1 on wide/height-bound screens):
  // level 1 still = fit, but level maxZoom lands at boardHalfH/maxZoom — the same
  // closeness a wide viewport gets at the top of the range.
  const effZoom = (): number => {
    const p = baseHalf() / boardHalfH; // ≥ 1; exactly 1 when height-bound
    return 1 + ((zoomLevel - 1) * (p * maxZoom - 1)) / (maxZoom - 1);
  };
  function applyCam(): void {
    const halfH = baseHalf() / effZoom();
    const hw = halfH * aspect;
    const slackX = Math.max(0, boardHalfW - hw);
    const slackY = Math.max(0, boardHalfH - halfH);
    cx = Math.min(playExt.cx + slackX, Math.max(playExt.cx - slackX, cx));
    cy = Math.min(playExt.cy + slackY, Math.max(playExt.cy - slackY, cy));
    camLeft = cx - hw; camRight = cx + hw; camTop = cy - halfH; camBot = cy + halfH;
  }
  applyCam();

  // world container = the camera transform (world units → CSS px)
  const world = new Container();
  app.stage.addChild(world);
  function syncCamera(): void {
    const sx = curW / (camRight - camLeft);
    const sy = curH / (camBot - camTop);
    world.scale.set(sx, sy);
    world.position.set(-camLeft * sx, -camTop * sy);
  }

  // layers, back → front
  const seaLayer = world.addChild(new Container());
  const foamLayer = world.addChild(new Graphics());
  const tileLayer = world.addChild(new Container());
  const beachLayer = world.addChild(new Graphics());
  const gridLayer = world.addChild(new Graphics());
  const sceneryLayer = world.addChild(new Container());
  const cityLayer = world.addChild(new Container());
  // The play-area border traces the outer edge of the hex grid so the sea doesn't
  // read as boundless. It sits BELOW the ships (and buoys/routes) so a hull that
  // overhangs the map edge is never bisected by the border line. Tradeoff: over
  // unexplored edge water the fog shrouds it, but the minimap still shows extent.
  const borderLayer = world.addChild(new Graphics());
  const shipLayer = world.addChild(new Container());
  const buoyLayer = world.addChild(new Container());
  const selectionLayer = world.addChild(new Graphics());
  const fogLayer = world.addChild(new Graphics());
  const fogMask = world.addChild(new Graphics());
  // Order lines draw ABOVE the fog so a course into unexplored water stays visible
  // instead of being swallowed by the shroud.
  const routeLayer = world.addChild(new Graphics());
  // City flags: the realistic ensign sprites live in flagSprites; the poles and
  // team glyphs draw in the flagLayer Graphics ABOVE them (pole over the cloth's
  // hoist edge reads as the flag bent onto the pole).
  const flagSprites = world.addChild(new Container());
  const flagLayer = world.addChild(new Graphics());
  // Dedicated overlay for the move-tween sprite. It sits above everything and is
  // NEVER cleared by buildShips(), so a board rebuild mid-slide (e.g. deselecting
  // the ship the instant it's sent) can't yank the sliding sprite out from under
  // the animation.
  const animLayer = world.addChild(new Container());

  // Ground-plane hex corners, vertically foreshortened by ISO_Y so tile/grid/fog
  // polygons match hc()'s tilted plane (hexCorners() itself stays a regular hex —
  // the top-down minimap still uses it).
  const isoCorners = (radius: number): [number, number][] =>
    hexCorners(radius).map(([x, y]) => [x, y * ISO_Y]);
  const HEXPTS = isoCorners(HEX);
  const hexPoly = (px: number, py: number, radius = HEX): number[] => {
    const pts = radius === HEX ? HEXPTS : isoCorners(radius);
    const out: number[] = [];
    for (const [x, y] of pts) out.push(px + x, py + y);
    return out;
  };

  // ---- static layers (built once; camera transform handles pan/zoom) --
  function placeSprite(layer: Container, texture: Texture, px: number, py: number, w: number, h: number, ay = 0.5): Sprite {
    const sp = layer.addChild(new Sprite(texture));
    sp.anchor.set(0.5, ay);
    sp.position.set(px, py);
    sp.width = w; sp.height = h;
    return sp;
  }

  function buildStatic(): void {
    // sea — flat depth colour base, CLIPPED to the play-grid hull so open water
    // stops at the border and everything beyond reads as out-of-bounds backdrop.
    // A seamless GREYSCALE tiling water texture draws OVER the flat base and is
    // TINTED to SEA_FILL, so hue comes entirely from SEA_FILL while the texture
    // contributes only ripple luminance; SEA_TEX_ALPHA then controls how strongly
    // that ripple detail reads over the otherwise-flat sea (lower = subtler).
    (seaLayer.addChild(new Graphics())).rect(ext.left, ext.top, ext.w, ext.h).fill(SEA_FILL);
    if (seaTex) {
      const ts = seaLayer.addChild(new TilingSprite({ texture: seaTex, width: ext.w, height: ext.h }));
      ts.position.set(ext.left, ext.top);
      ts.tileScale.set(SEA_TILE_WU / seaTex.width); // one tile = SEA_TILE_WU world units
      ts.tint = SEA_FILL; // colour the greyscale ripples with the base hue
      ts.alpha = SEA_TEX_ALPHA; // ripple-detail strength over the flat base
      seaSprite = ts; // kept for the drift animation (frame())
    }
    const seaMask = seaLayer.addChild(new Graphics());
    for (let r = 0; r < g.H; r++) {
      for (let c = 0; c < g.W; c++) { const [px, py] = hc(c, r); seaMask.poly(hexPoly(px, py), true).fill(0xffffff); }
    }
    seaLayer.mask = seaMask;

    // shore foam on the sea (land tiles drawn next cover its inland half)
    drawFoam();

    // land tiles — Scott's pre-cut hex grass render, drawn as a SPRITE per hex:
    // its fuzzy organic edge (grass overhanging the water) IS the coastline, so
    // no polygon clip. Slightly oversized so neighbouring tiles' fuzz overlaps
    // the seams; alternate hexes mirror + tint a touch darker for the classic
    // two-tone parity without a second texture.
    const grassTex = tex('assets/sprites/grass_tile.png');
    for (const [c, r] of landTiles) {
      const [px, py] = hc(c, r);
      const sp = placeSprite(tileLayer, grassTex, px, py, SQ3 * HEX * 1.05, 2 * HEX * 1.05 * ISO_Y);
      if ((c + r) % 2) { sp.scale.x = -sp.scale.x; sp.tint = 0xecf0ec; }
    }

    drawBeaches();
    drawGrid();
    drawBorder();

    // scenery (deterministic per-tile scatter, no RNG) — iso-diamond renders,
    // sized by width with the texture's own aspect (they're square-ish)
    const scenery = (url: string, px: number, py: number, w: number): void => {
      const t = tex(url);
      placeSprite(sceneryLayer, t, px, py, w, w / (t.width / t.height));
    };
    for (const [c, r] of inlandGrass) {
      const h = ((Math.imul(c, 73856093) ^ Math.imul(r, 19349663)) >>> 0) % 6;
      const [px, py] = hc(c, r);
      if (h === 0) scenery('assets/sprites/mountain.png', px, py - 3, 34);
      else if (h <= 2) scenery('assets/sprites/tree.png', px, py - 2, 32);
    }
    // cities: the walled-town overlay, drawn on top of the city hex's grass tile
    // (like a big piece of scenery). Sized to fill the hex and anchored near its
    // base so the town "sits" on the tile; sized by width with the sprite's own
    // aspect. Kept just within the hex so it doesn't overrun ships on neighbour tiles.
    const cityTex = tex('assets/sprites/city.png');
    const cityAspect = cityTex.width / cityTex.height;
    const cw = SQ3 * HEX * 0.88;
    for (const ct of g.cities) {
      const [px, py] = hc(ct.c, ct.r);
      placeSprite(cityLayer, cityTex, px, py - HEX * 0.08, cw, cw / cityAspect, 0.5);
    }
  }

  // Beaches: stroke sand along each tile's water-facing edges, joining runs into
  // polylines. The centreline sits ON the hex edge (rr = R) — not inset — so
  // (1) the band covers the grass tile's slight overhang toward the water (no grass
  // shows beyond the shore), and (2) adjacent coastal tiles' corner points coincide
  // at the shared hex vertex, so their shorelines meet instead of leaving gaps.
  function drawBeaches(): void {
    const R = HEX; const rr = R;
    const corner = (px: number, py: number, k: number): [number, number] => {
      const a = ((60 * k - 30) * Math.PI) / 180;
      return [px + rr * Math.cos(a), py + rr * Math.sin(a) * ISO_Y];
    };
    beachLayer.clear();
    for (const [c, r] of landTiles) {
      const [px, py] = hc(c, r);
      const water: boolean[] = [];
      for (let i = 0; i < 6; i++) { const [nc, nr] = edgeNeighbor(c, r, (6 - i) % 6); water[i] = isOpenWater(nc, nr); }
      const n = water.filter(Boolean).length;
      if (n === 0) continue;
      if (n === 6) {
        const pts: number[] = [];
        for (let k = 0; k < 6; k++) { const [vx, vy] = corner(px, py, k); pts.push(vx, vy); }
        beachLayer.poly(pts, true);
        continue;
      }
      let start = 0; while (water[start]) start++;
      let i = 0;
      while (i < 6) {
        const e = (start + i) % 6;
        if (!water[e]) { i++; continue; }
        const [sx, sy] = corner(px, py, e);
        beachLayer.moveTo(sx, sy);
        let j = i;
        while (j < 6 && water[(start + j) % 6]) { const [vx, vy] = corner(px, py, ((start + j) % 6 + 1) % 6); beachLayer.lineTo(vx, vy); j++; }
        i = j;
      }
    }
    // desaturated + slightly translucent sand with rounded corners, so the beach
    // reads as a soft edge. Wide enough that, centred on the hex edge, it covers the
    // grass overhang on the water side and its round caps overlap at shared vertices.
    // opaque (alpha 1): the runs overlap at shared corners (round caps), and any
    // translucency there stacks into a darker blob that reads as a "circle".
    beachLayer.stroke({ width: HEX * 0.10, color: '#ae9d6c', cap: 'round', join: 'round' });
  }

  // Shore foam: a surf band along water-facing coast edges, brightest right at the
  // shore and fading out into the sea. Drawn as MANY thin concentric rings on a
  // smooth alpha curve (rather than a few thick bands) so the fade reads as a
  // continuous gradient instead of visible steps. Rings abut exactly at each hex
  // corner (shared radial), so there's no double-alpha seam within a tile.
  function drawFoam(): void {
    const R = HEX;
    const pt = (px: number, py: number, k: number, rad: number): [number, number] => {
      const a = ((60 * k - 30) * Math.PI) / 180;
      return [px + rad * Math.cos(a), py + rad * Math.sin(a) * ISO_Y];
    };
    const RINGS = 24; const R_IN = 0.90; const R_OUT = 1.55; const A_MAX = 0.34; const FALLOFF = 1.6;
    foamLayer.clear();
    for (let b = 0; b < RINGS; b++) {
      const t0 = b / RINGS; const t1 = (b + 1) / RINGS;
      const rI = R * (R_IN + (R_OUT - R_IN) * t0);
      const rO = R * (R_IN + (R_OUT - R_IN) * t1);
      const alpha = A_MAX * Math.pow(1 - (t0 + t1) / 2, FALLOFF); // bright at shore → 0 out to sea
      if (alpha < 0.004) continue;
      for (const [c, r] of landTiles) {
        const [px, py] = hc(c, r);
        for (let i = 0; i < 6; i++) {
          const [nc, nr] = edgeNeighbor(c, r, (6 - i) % 6);
          if (!isOpenWater(nc, nr)) continue;
          const [i0x, i0y] = pt(px, py, i, rI);
          const [i1x, i1y] = pt(px, py, i + 1, rI);
          const [o0x, o0y] = pt(px, py, i, rO);
          const [o1x, o1y] = pt(px, py, i + 1, rO);
          foamLayer.poly([i0x, i0y, i1x, i1y, o1x, o1y, o0x, o0y], true).fill({ color: '#f4fdff', alpha });
        }
      }
    }
  }

  function drawGrid(): void {
    gridLayer.clear();
    for (let r = 0; r < g.H; r++) {
      for (let c = 0; c < g.W; c++) { const [px, py] = hc(c, r); gridLayer.poly(hexPoly(px, py), true); }
    }
    gridLayer.stroke({ width: 0.7, color: '#000000', alpha: 0.05 });
  }

  // Trace the map's outer edge: for every play tile, stroke each hex edge whose
  // neighbour falls outside the grid. This follows the true (zig-zag, odd-r) hull
  // rather than a rectangle, so the boundary reads exactly where the map ends. A
  // single thin grey line marks the rim — legible over both open sea and coastline
  // without a heavy dark halo. (Same edge/corner convention as drawBeaches.)
  function drawBorder(): void {
    const inGrid = (c: number, r: number): boolean => c >= 0 && c < g.W && r >= 0 && r < g.H;
    const trace = (): void => {
      for (let r = 0; r < g.H; r++) {
        for (let c = 0; c < g.W; c++) {
          const [px, py] = hc(c, r);
          for (let i = 0; i < 6; i++) {
            const [nc, nr] = edgeNeighbor(c, r, (6 - i) % 6);
            if (inGrid(nc, nr)) continue; // interior edge — skip
            const [ax, ay] = HEXPTS[i]!; const [bx, by] = HEXPTS[(i + 1) % 6]!;
            borderLayer.moveTo(px + ax, py + ay).lineTo(px + bx, py + by);
          }
        }
      }
    };
    borderLayer.clear();
    // A single thin grey rim — no dark halo pass underneath (the black outline read
    // as heavy against the backdrop).
    trace();
    borderLayer.stroke({ width: 1.0, color: '#adc2cd', alpha: 0.8, cap: 'round', join: 'round' });
  }

  // ---- dynamic state ---------------------------------------------------
  let dyn: BoardDynamic = { selectedId: opts.selectedId, reach: opts.reach, route: opts.route, routes: opts.routes, routeSel: opts.routeSel };
  let fogOn = opts.fog ?? true; // shroud on by default; the deploy screen turns it off
  let hideShipId: number | null = null;
  let lastDrawCalls = 0;

  // Signature of everything the fog/ship/flag/minimap/label layers derive from —
  // ship positions (+ sub state) and city owner/build. update() rebuilds those
  // (expensive: two full-board scans + a canvas redraw) only when this changes, so
  // a plain selection/reach change skips them entirely.
  function stateSig(): string {
    let s = '';
    for (const sh of g.ships) s += `${sh.id},${sh.c},${sh.r},${sh.submerged ? 1 : 0};`;
    for (const ct of g.cities) s += `|${ct.owner ?? '-'}`;
    return s;
  }
  let lastStateSig = '';

  function shipSize(type: Ship['type'], texture: Texture): [number, number] {
    const ratio = texture.width / texture.height;
    const L = HEX * 1.55 * (SHIP_SCALE[type] ?? 1);
    return [L, L / ratio];
  }

  // One ship = a small container: the base render, the white-ensign overlay
  // TINTED with the side colour (the art keeps the cloth white exactly so any
  // livery works), and the team glyph (R ring / Y cross — the same neutral
  // identity as the city flags) drawn on the cloth.
  function shipNode(s: Ship): Container {
    const node = new Container();
    const t = tex(shipSpriteUrl(s));
    const [w, h] = shipSize(s.type, t);
    const base = node.addChild(new Sprite(t));
    base.anchor.set(0.5);
    base.width = w; base.height = h;
    // submerged sub: the same render as a ghost — translucent with a cold blue
    // cast (multiply tint). The ensign still flies (ghosted with the hull) so
    // the sub's owner stays readable; only the hull gets the blue cast, a blue-
    // multiplied side colour would muddy the flag beyond recognition.
    const submerged = s.type === 'SS' && s.submerged;
    if (submerged) { base.alpha = 0.45; base.tint = '#a9c8e8'; }
    const facing = sideFacing(s.side);
    const ft = tex(ensignUrl(facing));
    const lay = ensignLayout(s.type, facing, w, h, ft.width / ft.height);
    if (lay) {
      const f = node.addChild(new Sprite(ft));
      f.anchor.set(facing === 'right' ? 1 : 0, 0); // pole-top corner sits on the mast
      f.position.set(lay.fx, lay.fy);
      f.width = lay.fw; f.height = lay.fh;
      f.tint = sideColor(s.side);
      const gfx = node.addChild(new Graphics());
      const { gx, gy, gr } = lay;
      if (s.side === 'R') gfx.circle(gx, gy, gr).stroke({ width: gr * 0.55, color: 'rgba(255,255,255,0.95)' });
      else gfx.moveTo(gx - gr, gy - gr).lineTo(gx + gr, gy + gr).moveTo(gx + gr, gy - gr).lineTo(gx - gr, gy + gr)
        .stroke({ width: gr * 0.55, color: 'rgba(36,24,4,0.9)', cap: 'round' });
      if (submerged) { f.alpha = 0.55; gfx.alpha = 0.55; }
    }
    return node;
  }

  function buildShips(): void {
    shipLayer.removeChildren();
    const visible = g.ships.filter((s) => visibleOnBoard(g, viewSide, s) && s.id !== hideShipId);
    const groups = new Map<string, Ship[]>();
    for (const s of visible) { const k = `${s.c},${s.r}`; (groups.get(k) ?? groups.set(k, []).get(k)!).push(s); }
    for (const [k, list] of groups) {
      const [c, r] = k.split(',').map(Number) as [number, number];
      const [bx, by] = hc(c, r);
      const n = list.length;
      list.forEach((s, i) => {
        const off = (i - (n - 1) / 2) * HEX * (n > 2 ? 0.5 : 0.8);
        const offY = (i % 2) * HEX * 0.3 - (n > 1 ? HEX * 0.15 : 0);
        shipLayer.addChild(shipNode(s)).position.set(bx + off, by + offY);
      });
    }
  }

  function buildBuoys(): void {
    buoyLayer.removeChildren();
    const s = HEX * 0.5;
    for (const [c, r, stop] of dyn.reach ?? []) {
      const [px, py] = hc(c, r);
      placeSprite(buoyLayer, tex(stop ? 'assets/sprites/buoy.png' : 'assets/sprites/buoy_grey.png'), px, py, s, s);
    }
  }

  /** Draw a course as an ARROW: the dotted-ish line, a small round marker on the
   *  FROM city, and a filled arrowhead pointing into the TO city. */
  function strokePath(gfx: Graphics, path: [number, number][], color: string, alpha = 1, width = 2): void {
    if (path.length < 2) return;
    path.forEach(([c, r], i) => { const [px, py] = hc(c, r); if (i) gfx.lineTo(px, py); else gfx.moveTo(px, py); });
    gfx.stroke({ width, color, alpha });
    // origin marker + arrowhead scale with the line width, so a thin (unselected)
    // route gets a small subtle marker and the prominent one a bigger head.
    const [ox, oy] = hc(path[0]![0], path[0]![1]);
    gfx.circle(ox, oy, width * 2).fill({ color, alpha });
    // arrowhead on the to city, oriented along the final segment
    const [ax, ay] = hc(path[path.length - 1]![0], path[path.length - 1]![1]);
    const [bx, by] = hc(path[path.length - 2]![0], path[path.length - 2]![1]);
    let dx = ax - bx; let dy = ay - by;
    const len = Math.hypot(dx, dy) || 1;
    dx /= len; dy /= len;
    const back = width * 5; const wing = width * 2.6; // arrowhead length + half-width
    const nx = -dy; const ny = dx; // perpendicular
    // Push the tip a little PAST the destination centre (where the shaft ends): at
    // the needle-thin apex the fill can't cover the full-width stroke, so the line
    // pokes through the point. Ending the shaft inside the head's wide part hides it.
    const ahead = width * 2;
    const tx = ax + dx * ahead; const ty = ay + dy * ahead;
    gfx.moveTo(tx, ty)
      .lineTo(tx - dx * back + nx * wing, ty - dy * back + ny * wing)
      .lineTo(tx - dx * back - nx * wing, ty - dy * back - ny * wing)
      .closePath()
      .fill({ color, alpha });
  }
  /** A dotted line along a hex path (evenly spaced dots), with a small hollow
   *  ring at the last tile — used for the selected move's white movement trail,
   *  visually distinct from the solid pale-yellow order arrows. */
  function dottedPath(gfx: Graphics, path: [number, number][], color: string): void {
    if (path.length < 2) return;
    const spacing = Math.max(6, HEX * 0.16); // world-units between dot centres
    const rad = Math.max(1.5, HEX * 0.035);
    for (let i = 1; i < path.length; i++) {
      const [ax, ay] = hc(path[i - 1]![0], path[i - 1]![1]);
      const [bx, by] = hc(path[i]![0], path[i]![1]);
      const len = Math.hypot(bx - ax, by - ay) || 1;
      const steps = Math.max(1, Math.round(len / spacing));
      for (let s = 0; s < steps; s++) {
        const t = s / steps;
        gfx.circle(ax + (bx - ax) * t, ay + (by - ay) * t, rad).fill({ color });
      }
    }
    const [ex, ey] = hc(path[path.length - 1]![0], path[path.length - 1]![1]);
    gfx.circle(ex, ey, rad * 2.2).stroke({ width: 2, color }); // ring at the destination
  }
  function buildRoutes(): void {
    routeLayer.clear();
    if (dyn.route) strokePath(routeLayer, dyn.route, '#ffffff');
    // Unselected order lines are thin + faint so the board doesn't clutter; the
    // selected ship's route draws prominent (full opacity, thicker) on top.
    for (const path of dyn.routes ?? []) strokePath(routeLayer, path, '#fff3cf', 0.5, 1);
    if (dyn.routeSel) strokePath(routeLayer, dyn.routeSel, '#fff3cf', 1, 2.5);
    if (dyn.trail) dottedPath(routeLayer, dyn.trail, '#ffffff');
    // lone origin dots: a chosen "from" port whose destination isn't set yet, so
    // the player sees the origin before the arrow completes.
    for (const [c, r] of dyn.marks ?? []) {
      const [px, py] = hc(c, r);
      routeLayer.circle(px, py, 5).fill({ color: '#fff3cf' });
      routeLayer.circle(px, py, 8.5).stroke({ width: 2, color: '#fff3cf' });
    }
  }

  function buildSelection(): void {
    selectionLayer.clear();
    if (dyn.selectedId == null) return;
    const s = g.ships.find((sh) => sh.id === dyn.selectedId);
    if (!s) return;
    const [px, py] = hc(s.c, s.r);
    // an ellipse (not a circle) so the selection ring lies flat on the tilted plane
    selectionLayer.ellipse(px, py, HEX * 0.78, HEX * 0.78 * ISO_Y).stroke({ width: HEX * 0.12, color: '#ffffff', alpha: 0.9 });
  }

  // Fog: a uniform dark shroud (a single rect over the whole board extent) CLIPPED
  // by a mask painted over the tiles the view side can't see. The mask is built from
  // one simple hex fill per hidden tile — NOT holes cut from a rect: cut()/hole
  // triangulation fans garbage wedges across the map once the visible tiles touch
  // each other, and an inverse mask silently no-ops here. A plain mask of separate
  // hexes is robust (each hex triangulates alone) and gives clean, seamless fog.
  // The mask covers ONLY play tiles, so the shroud ends exactly on the map's hull
  // (the border) instead of bleeding a ring of fog past it into the out-of-bounds
  // backdrop.
  function buildFog(): void {
    fogLayer.clear();
    fogMask.clear();
    if (!fogOn) { fogLayer.mask = null; return; }
    fogLayer.rect(ext.left, ext.top, ext.w, ext.h).fill({ color: 0x040c18, alpha: 0.66 });
    for (let r = 0; r < g.H; r++) {
      for (let c = 0; c < g.W; c++) {
        if (tileInView(g, viewSide, c, r)) continue; // visible tiles stay clear
        const [px, py] = hc(c, r);
        fogMask.poly(hexPoly(px, py), true).fill(0xffffff);
      }
    }
    fogLayer.mask = fogMask;
  }

  // City ownership flags: the SAME realistic ensign cloth the ships fly, tied
  // to the flag pole and tinted with the owner colour (neutral = grey), at the
  // old vector pennant's size. NAME/VP text is the DOM overlay below.
  function buildFlags(): void {
    flagLayer.clear();
    for (const c of flagSprites.removeChildren()) c.destroy();
    const LU = HEX; // flags are world-space, so a fixed world size (scales with zoom)
    const ensTex = tex(ensignUrl('left')); // hoist (pole edge) on the left
    for (const ct of g.cities) {
      const [px, py] = hc(ct.c, ct.r);
      const flagCol = ct.owner ? sideColor(ct.owner) : '#9aa0a6';
      const fw = LU * 0.52; const fh = fw / (ensTex.width / ensTex.height);
      const poleX = px - LU * 0.66; const topY = py - LU * 0.82;
      flagLayer.moveTo(poleX, topY - LU * 0.05).lineTo(poleX, topY + LU * 0.86)
        .stroke({ width: Math.max(0.6, LU * 0.05), color: '#2a3640', cap: 'round' });
      flagLayer.circle(poleX, topY - LU * 0.06, Math.max(0.8, LU * 0.055)).fill('#d8dde3');
      const f = flagSprites.addChild(new Sprite(ensTex));
      f.position.set(poleX, topY);
      f.width = fw; f.height = fh;
      f.tint = flagCol;
      if (ct.owner) {
        const gx = poleX + fw * 0.5; const gy = topY + fh * 0.52; const gr = fh * 0.3;
        const glyphCol = ct.owner === 'R' ? 'rgba(255,255,255,0.96)' : 'rgba(36,24,4,0.92)';
        if (ct.owner === 'R') flagLayer.circle(gx, gy, gr).stroke({ width: Math.max(0.6, fh * 0.16), color: glyphCol });
        else flagLayer.moveTo(gx - gr, gy - gr).lineTo(gx + gr, gy + gr).moveTo(gx + gr, gy - gr).lineTo(gx - gr, gy + gr)
          .stroke({ width: Math.max(0.6, fh * 0.16), color: glyphCol, cap: 'round' });
      }
    }
  }

  // ---- DOM label overlay (crisp native text — see geometry.ts notes) --
  const labelLayer = document.createElement('div');
  labelLayer.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none;overflow:hidden;z-index:2;';
  container.appendChild(labelLayer);
  const cityLabels: CityLabel[] = g.cities.map(() => {
    const plate = document.createElement('div');
    plate.style.cssText = `position:absolute;left:0;top:0;white-space:nowrap;line-height:1.1;font-family:${FONT_STACK};font-weight:400;font-size:12px;padding:2px 6px;background:${PALETTE.labelBg};border:1px solid ${PALETTE.labelEdge};border-radius:2px;will-change:transform;`;
    const name = document.createElement('span'); name.style.textShadow = TEXT_HALO;
    // points: the currency star + number (no brackets), set a little apart from the name.
    const vp = document.createElement('span'); vp.style.cssText = `color:#c7ecff;text-shadow:${TEXT_HALO};margin-left:0.4em;`;
    plate.append(name, vp);
    labelLayer.append(plate);
    return { plate, name, vp };
  });
  // Label text is fixed-size CSS px (like the rest of the HUD) — never scales
  // with zoom or viewport. labelUnit (screen px per hex at zoom 1) is only used
  // to keep a constant offset from the city; positions use the live camera below.
  const labelUnit = (): number => Math.max((HEX * curW) / (2 * baseHalf() * aspect), 30);
  function positionLabels(): void {
    const spanX = camRight - camLeft; const spanY = camBot - camTop; const LU = labelUnit();
    for (let i = 0; i < g.cities.length; i++) {
      const ct = g.cities[i]!; const lab = cityLabels[i]!;
      const [wx, wy] = hc(ct.c, ct.r);
      const csx = ((wx - camLeft) / spanX) * curW; const csy = ((wy - camTop) / spanY) * curH;
      lab.plate.style.transform = `translate(${csx}px, ${csy + LU * 0.5}px) translateX(-50%)`;
    }
  }
  function relabel(): void {
    for (let i = 0; i < g.cities.length; i++) {
      const ct = g.cities[i]!; const lab = cityLabels[i]!;
      lab.name.textContent = ct.name;
      lab.name.style.color = ct.home ? sideColorLight(ct.home) : PALETTE.labelText;
      lab.vp.innerHTML = `${ICON_VP}${ct.vp}`;
    }
  }

  // ---- minimap (Canvas2D HUD thumbnail, DOM overlay bottom-right) ------
  const miniW = 180;
  const miniCanvas = document.createElement('canvas');
  miniCanvas.style.cssText = 'position:absolute;right:6px;bottom:6px;pointer-events:none;border:2px solid #c0c4cc;z-index:2;image-rendering:auto;';
  miniCanvas.style.width = `${miniW}px`;
  miniCanvas.style.height = `${minimapSize(g, miniW).h}px`;
  container.appendChild(miniCanvas);
  const refreshMinimap = (): void => drawMinimap(miniCanvas, g, viewSide, miniW, fogOn);

  // ---- move tween (ship glide) ----------------------------------------
  interface Tween { sprite: Container; pts: [number, number][]; segLen: number[]; total: number; startT: number; dur: number; onDone: () => void; }
  let tween: Tween | null = null;
  function finishTween(): void {
    if (!tween) return;
    const t = tween; tween = null;
    t.sprite.destroy({ children: true });
    hideShipId = null;
    buildShips();
    if (seaAnimate) app.ticker.maxFPS = SEA_IDLE_FPS; // tween over → back to lazy sea drift
    else app.ticker.stop(); // no sea drift → back to render-on-demand
    renderOnce();
    t.onDone();
  }
  function stepTween(): void {
    if (!tween) return;
    const k = Math.min(1, (performance.now() - tween.startT) / tween.dur);
    let d = k * tween.total;
    let px = tween.pts[0]![0]; let py = tween.pts[0]![1];
    for (let i = 0; i < tween.segLen.length; i++) {
      if (d <= tween.segLen[i]! || i === tween.segLen.length - 1) {
        const f = tween.segLen[i] ? d / tween.segLen[i]! : 0;
        const a = tween.pts[i]!; const b = tween.pts[i + 1]!;
        px = a[0] + (b[0] - a[0]) * f; py = a[1] + (b[1] - a[1]) * f;
        break;
      }
      d -= tween.segLen[i]!;
    }
    tween.sprite.position.set(px, py);
    if (k >= 1) finishTween();
  }

  // ---- frame: runs while a move tween is active OR the sea is drifting. The
  // camera is fixed during both (no pan/zoom), so labels needn't reposition — only
  // the tweened sprite + the water tile move, and Pixi auto-renders the stage each
  // tick the ticker is running. ---------------
  function frame(): void {
    stepTween();
    if (seaSprite) {
      // ticker.deltaMS → seconds, so drift is framerate-independent.
      const dt = app.ticker.deltaMS / 1000;
      seaSprite.tilePosition.x += SEA_DRIFT_X * dt;
      seaSprite.tilePosition.y += SEA_DRIFT_Y * dt;
    }
  }
  function renderOnce(): void {
    syncCamera();
    positionLabels();
    app.render();
    lastDrawCalls = world.children.length;
  }

  // initial build
  buildStatic();
  buildShips();
  buildBuoys();
  buildRoutes();
  buildSelection();
  buildFog();
  buildFlags();
  refreshMinimap();
  relabel();
  syncCamera();
  positionLabels();
  lastStateSig = stateSig();
  // Render on demand: the ticker is registered but stays stopped while idle; it is
  // started only for a move tween (animateMove → finishTween). Under automation
  // wantAnim is false so it never starts — the frozen golden path is unchanged.
  app.ticker.add(frame);
  app.ticker.stop();
  // Sea drift wants a continuously-running ticker; without an active tween the
  // board is otherwise idle, so it runs at the low SEA_IDLE_FPS. (Automation
  // leaves seaAnimate false → stays frozen.)
  if (seaAnimate) { app.ticker.maxFPS = SEA_IDLE_FPS; app.ticker.start(); }
  app.render();

  const rerender = renderOnce;

  return {
    renderer: { domElement: canvas },
    renderOnce,
    drawCalls: () => { renderOnce(); return lastDrawCalls; },
    zoom: (factor) => { zoomLevel = factor; applyCam(); syncCamera(); relabel(); positionLabels(); rerender(); },
    panPixels: (dxPx, dyPx) => {
      const worldPerPx = ((baseHalf() / effZoom()) * 2) / curH;
      cx -= dxPx * worldPerPx; cy -= dyPx * worldPerPx;
      applyCam(); syncCamera(); positionLabels(); rerender();
    },
    resize: (w, h) => {
      if (w <= 0 || h <= 0) return;
      curW = w; curH = h; aspect = w / h;
      app.renderer.resize(w, h);
      applyCam(); syncCamera(); relabel(); positionLabels(); rerender();
    },
    setMinimapShown: (shown) => { miniCanvas.style.display = shown ? 'block' : 'none'; },
    update: (next) => {
      // Fog toggle persists when omitted; rebuild the shroud if it flips even when
      // nothing else changed (e.g. deploy → play with the same ship positions).
      const fogChanged = next.fog !== undefined && next.fog !== fogOn;
      if (next.fog !== undefined) fogOn = next.fog;
      dyn = { selectedId: next.selectedId, reach: next.reach, route: next.route, trail: next.trail, routes: next.routes, routeSel: next.routeSel, marks: next.marks };
      // Light layers — change on every click/hover, cheap to rebuild.
      buildBuoys(); buildRoutes(); buildSelection();
      // Heavy layers — only when ship positions or city owner/build actually moved.
      const sig = stateSig();
      if (sig !== lastStateSig) {
        lastStateSig = sig;
        buildShips(); buildFog(); buildFlags(); refreshMinimap(); relabel();
      } else if (fogChanged) {
        buildFog(); refreshMinimap();
      }
      positionLabels(); rerender();
    },
    animateMove: (shipId, path, onDone) => {
      const s = g.ships.find((x) => x.id === shipId);
      if (!wantAnim || !s || path.length < 2) { onDone(); return; }
      if (tween) finishTween();
      const pts = path.map(([c, r]) => hc(c, r));
      const segLen: number[] = []; let total = 0;
      for (let i = 0; i < pts.length - 1; i++) { const dx = pts[i + 1]![0] - pts[i]![0]; const dy = pts[i + 1]![1] - pts[i]![1]; const d = Math.hypot(dx, dy); segLen.push(d); total += d; }
      hideShipId = shipId;
      buildShips();
      const sprite = animLayer.addChild(shipNode(s));
      sprite.position.set(pts[0]![0], pts[0]![1]);
      tween = { sprite, pts, segLen, total, startT: performance.now(), dur: Math.min(700, 150 * (pts.length - 1) + 120), onDone };
      app.ticker.maxFPS = 0; // full frame rate for a smooth glide (finishTween drops back to idle/stopped)
      app.ticker.start(); // run the loop only for the tween's lifetime
    },
    dispose: () => { app.destroy(true, { children: true }); labelLayer.remove(); miniCanvas.remove(); },
    screenToHex: (clientX, clientY) => {
      const rect = canvas.getBoundingClientRect();
      const nx = (clientX - rect.left) / rect.width; const ny = (clientY - rect.top) / rect.height;
      if (nx < 0 || nx > 1 || ny < 0 || ny > 1) return null;
      const wx = camLeft + nx * (camRight - camLeft); const wy = camTop + ny * (camBot - camTop);
      // pixel → fractional axial (pointy-top, size HEX) → cube round → odd-r offset,
      // O(1) instead of scanning every tile. Un-foreshorten wy first (hc scales it by
      // ISO_Y) so the axial math sees the untilted grid it was derived for.
      const wyU = wy / ISO_Y;
      const x = (SQ3 / 3 * wx - wyU / 3) / HEX; const z = (2 / 3 * wyU) / HEX; const y = -x - z;
      let rx = Math.round(x); const ry = Math.round(y); let rz = Math.round(z);
      const dx = Math.abs(rx - x); const dy = Math.abs(ry - y); const dz = Math.abs(rz - z);
      if (dx > dy && dx > dz) rx = -ry - rz; else if (!(dy > dz)) rz = -rx - ry;
      let row = rz; let col = rx + (rz - (rz & 1)) / 2;
      if (row < 0 || row >= g.H || col < 0 || col >= g.W) {
        // Off the play grid (the decorative border ring): the rounded hex is the
        // global-nearest centre but out of bounds — fall back to the nearest
        // in-bounds centre so an edge click still snaps. Rare, so the scan is fine.
        let bd = Infinity; row = -1; col = -1;
        for (let r = 0; r < g.H; r++) {
          for (let c = 0; c < g.W; c++) { const [hx, hy] = hc(c, r); const d = (hx - wx) ** 2 + (hy - wy) ** 2; if (d < bd) { bd = d; row = r; col = c; } }
        }
      }
      // guard far clicks: the point must land within the hex it resolved to
      const [hx, hy] = hc(col, row);
      return (hx - wx) ** 2 + (hy - wy) ** 2 <= HEX * HEX * 1.3 ? [col, row] : null;
    },
    hexToScreen: (c, r) => {
      const [wx, wy] = hc(c, r);
      const nx = (wx - camLeft) / (camRight - camLeft); const ny = (wy - camTop) / (camBot - camTop);
      const rect = canvas.getBoundingClientRect();
      return [rect.left + nx * rect.width, rect.top + ny * rect.height];
    },
  };
}
